import {
  LOW_RESOLUTION_NOTICE,
  MIN_RELIABLE_TEXT_HEIGHT_PX,
} from '../constants';
import { prepareOcrCanvas, releaseCanvas, type DecodedSource, type PrepareOptions } from '../preprocess';
import { medianTokenHeight, reconstructLayout, type OcrToken } from '../layout';
import {
  OcrError,
  averageOf,
  toOcrError,
  type OcrLine,
  type OcrResult,
  type OcrRunContext,
  type StatusCallback,
} from '../types';

export interface NextGenStatus {
  available: boolean | null;
  mode: 'worker' | 'main' | null;
  backend: string | null;
  blocker: string | null;
  modelSource: string;
}

const MODEL_SOURCE = 'PP-OCRv5 (en) · official @paddleocr/paddleocr-js SDK, ONNX Runtime Web WASM';

const status: NextGenStatus = {
  available: null,
  mode: null,
  backend: null,
  blocker: null,
  modelSource: MODEL_SOURCE,
};

let engine: any = null;
let initPromise: Promise<any> | null = null;
let chain: Promise<unknown> = Promise.resolve();

function serial<T>(fn: () => Promise<T>): Promise<T> {
  const run = chain.then(fn, fn);
  chain = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

export function getNextGenStatus(): NextGenStatus {
  return { ...status };
}

export async function disposeNextGen(): Promise<void> {
  const current = engine;
  engine = null;
  initPromise = null;
  if (current?.dispose) {
    try {
      await current.dispose();
    } catch (e) {
      /* already released */
    }
  }
}

const ATTEMPTS: { worker: boolean; label: 'worker' | 'main' }[] = [
  { worker: true, label: 'worker' },
  { worker: false, label: 'main' },
];

/**
 * Lazily loads the official @paddleocr/paddleocr-js SDK (PP-OCRv5) in the
 * browser only. Worker mode is preferred; single-threaded WASM on the main
 * thread is the compatibility fallback. No COOP/COEP headers are required.
 * This function never falls back to Tesseract.
 */
export async function initNextGen(onStatus?: StatusCallback): Promise<any> {
  if (engine) return engine;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    onStatus?.({ text: 'Loading Next-Gen OCR runtime', progress: 0.05 });
    let PaddleOCR: any;
    try {
      const mod = await import('@paddleocr/paddleocr-js');
      PaddleOCR = mod.PaddleOCR;
      if (!PaddleOCR) throw new Error('PaddleOCR export not found in @paddleocr/paddleocr-js');
    } catch (e) {
      status.available = false;
      status.blocker = `SDK import failed: ${(e as Error)?.message || String(e)}`;
      throw new OcrError(
        'ENGINE_UNAVAILABLE',
        'Next-Gen OCR could not be loaded in this browser.',
        status.blocker,
        'nextgen',
      );
    }

    const failures: string[] = [];
    for (const attempt of ATTEMPTS) {
      try {
        onStatus?.({
          text: attempt.worker ? 'Starting Next-Gen worker' : 'Starting Next-Gen (compatibility mode)',
          progress: 0.15,
        });
        // eslint-disable-next-line no-await-in-loop
        const created = await PaddleOCR.create({
          lang: 'en',
          ocrVersion: 'PP-OCRv5',
          worker: attempt.worker,
          ortOptions: { backend: 'wasm', numThreads: 1, simd: true },
        });
        onStatus?.({ text: 'Downloading PP-OCRv5 models', progress: 0.35 });
        // eslint-disable-next-line no-await-in-loop
        if (typeof created.initialize === 'function') await created.initialize();
        let summary: any = null;
        try {
          summary = created.getInitializationSummary?.();
        } catch (e) {
          /* optional */
        }
        engine = created;
        status.available = true;
        status.mode = attempt.label;
        status.backend = summary?.runtime?.provider || summary?.runtime?.backend || 'wasm';
        status.blocker = null;
        onStatus?.({ text: 'Next-Gen OCR ready', progress: 0.6 });
        return created;
      } catch (e) {
        failures.push(`${attempt.label}: ${(e as Error)?.message || String(e)}`);
      }
    }

    status.available = false;
    status.mode = null;
    status.blocker = failures.join(' | ');
    throw new OcrError(
      'ENGINE_UNAVAILABLE',
      'Next-Gen OCR could not start in this browser.',
      status.blocker,
      'nextgen',
    );
  })();

  try {
    return await initPromise;
  } finally {
    initPromise = null;
  }
}

function polyToBox(poly: number[][] | null | undefined) {
  if (!poly || !poly.length) return { x: 0, y: 0, width: 1, height: 1 };
  const xs = poly.map((p) => p[0]);
  const ys = poly.map((p) => p[1]);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return {
    x,
    y,
    width: Math.max(1, Math.max(...xs) - x),
    height: Math.max(1, Math.max(...ys) - y),
  };
}

export interface NextGenRecognizeInput {
  source: DecodedSource;
  prepare: PrepareOptions;
}

/**
 * Recognises with PaddleOCR PP-OCRv5 only. On any failure it throws so the UI
 * can offer an explicit "Retry with Classic OCR" action — it never silently
 * returns a Tesseract result labelled as Next-Gen.
 */
export async function nextGenRecognize(
  input: NextGenRecognizeInput,
  ctx: OcrRunContext,
  onStatus?: StatusCallback,
): Promise<OcrResult> {
  if (ctx.languageId !== 'english') {
    throw new OcrError(
      'ENGINE_UNAVAILABLE',
      'Next-Gen OCR is currently available only for English in this app.',
      undefined,
      'nextgen',
    );
  }
  const started = Date.now();
  const ocr = await initNextGen(onStatus);
  if (ctx.isStale()) throw new OcrError('STALE_RUN', 'This run was superseded.');

  return serial(async () => {
    if (ctx.isStale()) throw new OcrError('STALE_RUN', 'This run was superseded.');
    let canvas: HTMLCanvasElement | null = null;
    try {
      onStatus?.({ text: 'Recognising text with PP-OCRv5', progress: 0.7 });
      const prepared = prepareOcrCanvas(input.source, { ...input.prepare, variant: 'upscaled' });
      canvas = prepared.canvas;
      const results = await ocr.predict(canvas, { textRecScoreThresh: 0.3 });
      const result = Array.isArray(results) ? results[0] : results;
      const items: any[] = result?.items || [];
      const pageNumber = ctx.pageNumber ?? 1;

      const tokens: OcrToken[] = items
        .filter((it) => (it.text || '').trim())
        .map((it) => ({
          text: it.text.trim(),
          confidence: typeof it.score === 'number' ? it.score * 100 : null,
          boundingBox: polyToBox(it.poly),
          page: pageNumber,
          engine: 'nextgen',
        }));

      if (ctx.isStale()) throw new OcrError('STALE_RUN', 'This run was superseded.');

      const layout = reconstructLayout(tokens);
      const text = layout.text.normalize('NFC');
      if (!text.trim()) {
        throw new OcrError(
          'NO_TEXT_FOUND',
          'Next-Gen OCR found no readable text. Try Classic OCR or the other recognition mode.',
          undefined,
          'nextgen',
        );
      }

      const canvasTextHeight = medianTokenHeight(tokens);
      const medianSourceTextHeight = prepared.scale > 0 ? canvasTextHeight / prepared.scale : null;
      const lowResolution =
        medianSourceTextHeight !== null && medianSourceTextHeight < MIN_RELIABLE_TEXT_HEIGHT_PX;

      const warnings: string[] = [];
      if (layout.tables.length) {
        warnings.push(
          `${layout.tables.length} table-like block${
            layout.tables.length > 1 ? 's' : ''
          } reconstructed into rows and columns.`,
        );
      }
      if (lowResolution) warnings.push(LOW_RESOLUTION_NOTICE);

      const lines: OcrLine[] = layout.lines.map((line) => ({
        text: line.text.normalize('NFC'),
        confidence: line.confidence,
        bbox: {
          x0: Math.min(...line.tokens.map((t) => t.boundingBox.x)),
          y0: line.top,
          x1: Math.max(...line.tokens.map((t) => t.boundingBox.x + t.boundingBox.width)),
          y1: line.bottom,
        },
        pageNumber,
      }));

      return {
        text,
        lines,
        tokens,
        tables: layout.tables,
        averageConfidence: averageOf(tokens.map((t) => t.confidence).filter((c): c is number => c !== null)),
        engine: 'nextgen',
        engineLabel: `Next-Gen OCR · PaddleOCR PP-OCRv5 (${status.mode || 'wasm'} mode)`,
        languageId: ctx.languageId,
        languageConfig: ['en (PP-OCRv5)'],
        modelSource: MODEL_SOURCE,
        mode: ctx.mode,
        preprocessVariant: prepared.meta.variant,
        segmentationMode: 'PP-OCRv5 detection + recognition',
        durationMs: Date.now() - started,
        pageNumber: ctx.pageNumber ?? null,
        invertedForDarkBackground: prepared.meta.inverted,
        medianSourceTextHeight,
        lowResolution,
        warnings,
      };
    } catch (e) {
      const err = toOcrError(e);
      err.engine = 'nextgen';
      throw err;
    } finally {
      releaseCanvas(canvas);
    }
  });
}
